import { call, put, takeLatest } from "redux-saga/effects";
import { POS_DASHBOARD_GO_BUTTON_ACTION } from "./actionType";
import {
    PosDashboard_GoButton_Success,
    PosDashboard_ApiErrorAction,
} from "./action";
import { PosDashboardDetailsAPI } from "../../../helpers/backend_helper";
import { loginSystemSetting } from "../../../components/Common/CommonFunction";

/* ============================================================
   FORMATTERS
============================================================ */
function formatDateTime(dateStr) {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yyyy = date.getFullYear();
    let hh = date.getHours();
    const mins = String(date.getMinutes()).padStart(2, "0");
    const ampm = hh >= 12 ? "PM" : "AM";
    hh = hh % 12 || 12;
    return `${dd}-${mm}-${yyyy} (${hh}:${mins} ${ampm})`;
}

/* ============================================================
   LOCAL MIRRORS
   These duplicate pure functions from posDashboard.logic.js so the
   saga can compute the same checks server-side while building the
   response. Keep BOTH copies in sync whenever the rule changes.
============================================================ */

// mirrors isDateMismatch() in posDashboard.logic.js — used elsewhere
// (ERP invoice/delete chips), NOT part of the IsOk calc below.
function isDateMismatchLocal(posDate, erpDate, allowedMinutes) {
    if (!posDate) return false;
    if (!erpDate) return true;

    const limit =
        allowedMinutes === "" || allowedMinutes === null || allowedMinutes === undefined
            ? 10
            : Number(allowedMinutes);

    const diffMinutes = Math.abs(new Date(posDate).getTime() - new Date(erpDate).getTime()) / (1000 * 60);
    return diffMinutes > limit;
}

// mirrors isUpdateRequired() in posDashboard.logic.js
function isUpdateRequiredLocal(running, current) {
    if (!running || !current) return false;
    return running !== current;
}

// Used only by the IsOk invoice-sync checks (B4/B5 below).
// "Correct" means: a POS-side date exists, an ERP-side date exists,
// and the gap between them is under the threshold.
//   - no POS date at all  => nothing to check => treated as correct
//   - POS date exists but no ERP date          => NOT correct
function isWithinSyncFrequencyLocal(posDate, erpDate, thresholdMinutes) {
    if (!posDate) return true;
    if (!erpDate) return false;
    const diffMinutes = Math.abs(new Date(posDate).getTime() - new Date(erpDate).getTime()) / (1000 * 60);
    return diffMinutes < thresholdMinutes;
}

// mirrors normalizeInvoiceNo() in posDashboard.logic.js — used only by the
// new B6 invoice/delete-number check below.
function normalizeInvoiceNoLocal(v) {
    return (v ?? "").toString().trim();
}


/* ============================================================
   MAIN SAGA
============================================================ */
function* PosDashboard_GenFunc({ config }) {
    try {
        // Reference point for every "gap since last log" calculation below.
        // Captured once, right when Go is clicked/processed — NOT
        // recalculated per-row later — so every machine's gap is measured
        // against the same instant.
        const goClickTime = Date.now();

        const response = yield call(PosDashboardDetailsAPI, config);

        /* ========================================================
           STEP 1 — FLATTEN
           Turn the nested { Party -> Clients[] } shape into one
           flat array of client rows. Party-level metadata
           (TotalClients, version-mismatch counts, and the three
           minute-based frequency settings) is copied onto every
           client row it applies to.
        ======================================================== */
        const flatData = [];

        (response?.Data || []).forEach(partyBlock => {
            (partyBlock.Clients || []).forEach(client => {
                // "All" = Active + IsShowPOSDashboard only
                // if (Number(client.Active) !== 1) return;
                // if (Number(client.IsShowPOSDashboard) !== 1) return;

                flatData.push({
                    ...client,
                    Party: partyBlock.Party,
                    TotalClients: partyBlock.TotalClients,
                    PosVersionMismatch: partyBlock.PosVersionMismatch,
                    ServiceVersionMismatch: partyBlock.ServiceVersionMismatch,
                    SyncTime: Number(partyBlock?.SyncTime) || 10,
                    POSDasboardSyncFrequency: Number(partyBlock?.POSDasboardSyncFrequency),
                });
            });
        });

        /* ========================================================
           STEP 2 — GROUP BY ClientID (one row per physical machine)
           Used for every view EXCEPT the "Service Not Working"
           sub-tab (see STEP 2B below, which deliberately does NOT
           merge — that sub-tab needs each SERVICE client visible
           as its own separate row, not folded into its machine's
           POS row).
        ======================================================== */
        const merged = {};

        flatData.forEach(item => {
            const key = item.ClientID;

            if (!merged[key]) {
                merged[key] = {
                    ClientID: item.ClientID,
                    Party: item.Party,
                    IsServerMachine: 0,
                    IsOffline: 0,
                    IsOk: 0,
                    InvoiceSyncPending: 0,
                    DeleteSyncPending: 0,
                    VersionMismatchList: [],
                    MacAddress: item.MacAddress,
                    Versions: [],
                    HasSyncIssue: false,
                    MaxInvoiceID: item.MaxInvoiceID,
                    MaxDeletedID: item.MaxDeletedID,
                    SyncTime: Number(item?.SyncTime) || 10,
                    POSDasboardSyncFrequency: Number(item?.POSDasboardSyncFrequency),
                    MaxInvoiceCreatedOn: null,
                    ERPMaxInvoiceCreatedOn: null,
                    MaxDeletedInvoiceCreatedOn: null,
                    ERPDeleteMaxInvoiceCreatedOn: null,
                    MaxInvoiceFullInvoiceNo: null,
                    ERPMaxInvoiceFullInvoiceNo: null,
                    MaxDeletedInvoiceFullInvoiceNo: null,
                    ERPDeleteMaxInvoiceFullInvoiceNo: null,
                    // each side's OWN invoice/delete timestamp (separate from
                    // the ERP-compare fields above). Used only by "Bill Time
                    // Wise" to pick whichever side is more recent, then compare
                    // that against "now".
                    PosMaxInvoiceCreatedOn: null,
                    ServiceMaxInvoiceCreatedOn: null,
                    PosMaxDeletedInvoiceCreatedOn: null,
                    ServiceMaxDeletedInvoiceCreatedOn: null,
                    // raw POS / Service items kept for Step 3's LastSync comparison
                    PosItem: null,
                    ServiceItem: null,
                };
            }

            if (Number(item.IsServer) === 1) {
                merged[key].IsServerMachine = 1;
            }

            if (Number(item.InvoiceSyncPending) === 1) {
                merged[key].InvoiceSyncPending = 1;
            }
            if (Number(item.DeleteSyncPending) === 1) {
                merged[key].DeleteSyncPending = 1;
            }

            if (item.OverallSyncStatus && item.OverallSyncStatus !== "OK") {
                const rawStatus = item.OverallSyncStatus;
                const sideLabel = item.ClientType === "SERVICE" ? "SERVICE" : "POS";
                const status = rawStatus.toUpperCase().includes("MISMATCH")
                    ? `${sideLabel} VERSION MISMATCH`
                    : rawStatus;

                if (!merged[key].VersionMismatchList.includes(status)) {
                    merged[key].VersionMismatchList.push(status);
                }
            }

            if (item.MaxInvoiceCreatedOn) {
                const prev = merged[key].MaxInvoiceCreatedOn;
                if (!prev || new Date(item.MaxInvoiceCreatedOn) > new Date(prev)) {
                    merged[key].MaxInvoiceCreatedOn = item.MaxInvoiceCreatedOn;
                    merged[key].MaxInvoiceFullInvoiceNo = item.MaxInvoiceFullInvoiceNo;
                }
            }
            if (item.ERPMaxInvoiceCreatedOn) {
                const prev = merged[key].ERPMaxInvoiceCreatedOn;
                if (!prev || new Date(item.ERPMaxInvoiceCreatedOn) > new Date(prev)) {
                    merged[key].ERPMaxInvoiceCreatedOn = item.ERPMaxInvoiceCreatedOn;
                    merged[key].ERPMaxInvoiceFullInvoiceNo = item.ERPMaxFullInvoiceNo;
                }
            }
            if (item.MaxDeletedInvoiceCreatedOn) {
                const prev = merged[key].MaxDeletedInvoiceCreatedOn;
                if (!prev || new Date(item.MaxDeletedInvoiceCreatedOn) > new Date(prev)) {
                    merged[key].MaxDeletedInvoiceCreatedOn = item.MaxDeletedInvoiceCreatedOn;
                    merged[key].MaxDeletedInvoiceFullInvoiceNo = item.MaxDeletedInvoiceFullInvoiceNo;
                }
            }
            if (item.ERPDeleteMaxInvoiceCreatedOn) {
                const prev = merged[key].ERPDeleteMaxInvoiceCreatedOn;
                if (!prev || new Date(item.ERPDeleteMaxInvoiceCreatedOn) > new Date(prev)) {
                    merged[key].ERPDeleteMaxInvoiceCreatedOn = item.ERPDeleteMaxInvoiceCreatedOn;
                    merged[key].ERPDeleteMaxInvoiceFullInvoiceNo = item.ERPDeleteMaxFullInvoiceNo;
                }
            }

            // each side's own raw timestamp, kept separately (no merging/
            // comparing here) so "Bill Time Wise" can pick whichever side is
            // more recent later.
            if (item.ClientType === "POS" && item.MaxInvoiceCreatedOn) {
                merged[key].PosMaxInvoiceCreatedOn = item.MaxInvoiceCreatedOn;
            }
            if (item.ClientType === "SERVICE" && item.MaxInvoiceCreatedOn) {
                merged[key].ServiceMaxInvoiceCreatedOn = item.MaxInvoiceCreatedOn;
            }
            if (item.ClientType === "POS" && item.MaxDeletedInvoiceCreatedOn) {
                merged[key].PosMaxDeletedInvoiceCreatedOn = item.MaxDeletedInvoiceCreatedOn;
            }
            if (item.ClientType === "SERVICE" && item.MaxDeletedInvoiceCreatedOn) {
                merged[key].ServiceMaxDeletedInvoiceCreatedOn = item.MaxDeletedInvoiceCreatedOn;
            }

            /* ---------- Versions: BOTH POS and Service show up ---------- */
            merged[key].Versions.push({
                label: item.ClientType === "SERVICE" ? "Service" : "POS",
                running: item.AppVersion,
                current: item.CurrentVersion,
                status: item.OverallSyncStatus,
                lastSync: item.LastSyncDateTime || null,
                // raw source fields so downstream logic (isServiceNotWorking)
                // can check the actual API flags instead of the display label.
                clientType: item.ClientType,
                isService: Number(item.IsService) === 1,
            });

            if (item.ClientType === "SERVICE") {
                merged[key].ServiceItem = item;
            } else {
                merged[key].PosItem = item;
            }
        });

        /* ========================================================
           STEP 2B — SERVICE-ONLY ROWS (NOT merged with POS)
           Purely for the "Service Not Working" sub-tab. Every raw
           client record where ClientType === "SERVICE" && IsService
           === 1 becomes its own standalone row — same machine can
           already appear once in `merged` (POS+Service combined)
           AND once here (Service alone); the two lists are used by
           different views and never mixed together.
        ======================================================== */
        const serviceOnlyRows = flatData
            .filter(item => item.ClientType === "SERVICE" && Number(item.IsService) === 1)
            .map(item => {
                const versionMismatchList = [];

                if (item.OverallSyncStatus && item.OverallSyncStatus !== "OK") {
                    const rawStatus = item.OverallSyncStatus;
                    versionMismatchList.push(
                        rawStatus.toUpperCase().includes("MISMATCH")
                            ? "SERVICE VERSION MISMATCH"
                            : rawStatus
                    );
                }
                if (Number(item.IsOffline) === 1) {
                    versionMismatchList.push("OFFLINE");
                }
                if (versionMismatchList.length === 0) {
                    versionMismatchList.push("OK");
                }

                return {
                    ClientID: item.ClientID,
                    Party: item.Party,
                    IsServerMachine: Number(item.IsServer) === 1 ? 1 : 0,
                    IsOffline: Number(item.IsOffline) === 1 ? 1 : 0,
                    MacAddress: item.MacAddress,
                    SyncTime: Number(item?.SyncTime) || 10,
                    VersionMismatchList: versionMismatchList,
                    Versions: [{
                        label: "Service",
                        running: item.AppVersion,
                        current: item.CurrentVersion,
                        status: item.OverallSyncStatus,
                        lastSync: item.LastSyncDateTime || null,
                        clientType: item.ClientType,
                        isService: true,
                    }],
                    CreatedOnList: item.LastSyncDateTime ? [formatDateTime(item.LastSyncDateTime)] : [],
                    PosLastSync: null,
                    ServiceLastSync: item.LastSyncDateTime ? formatDateTime(item.LastSyncDateTime) : null,
                    PosLastSyncRaw: null,
                    ServiceLastSyncRaw: item.LastSyncDateTime || null,
                    LastSeenAt: item.LastSyncDateTime || null,
                    // flags this row as an unmerged, service-only entry so the
                    // UI/logic layer can tell it apart from a normal merged row
                    IsServiceOnlyRow: true,
                };
            });

        /* ========================================================
           STEP 3 — FINALIZE each MERGED machine row
        ======================================================== */
        Object.values(merged).forEach(entry => {
            const posItem = entry.PosItem;
            const serviceItem = entry.ServiceItem;

            /* ---------- 3.1 Versions list + Last Synced display fields ---------- */
            entry.Versions.sort((a, b) => {
                const order = { POS: 0, Service: 1 };
                return order[a.label] - order[b.label];
            });

            entry.CreatedOnList = [];
            if (posItem?.LastSyncDateTime) {
                entry.CreatedOnList.push(formatDateTime(posItem.LastSyncDateTime));
            }
            if (serviceItem?.LastSyncDateTime) {
                entry.CreatedOnList.push(formatDateTime(serviceItem.LastSyncDateTime));
            }
            entry.PosLastSync = posItem?.LastSyncDateTime
                ? formatDateTime(posItem.LastSyncDateTime)
                : null;
            entry.ServiceLastSync = serviceItem?.LastSyncDateTime
                ? formatDateTime(serviceItem.LastSyncDateTime)
                : null;
            entry.PosLastSyncRaw = posItem?.LastSyncDateTime || null;
            entry.ServiceLastSyncRaw = serviceItem?.LastSyncDateTime || null;

            /* ---------- 3.2 LastSeenAt / latestRawItem
               Both derived the same way: compare posItem's vs serviceItem's
               LastSyncDateTime, take the newer one (or whichever single one
               exists). LastSeenAt is the timestamp; latestRawItem is the
               actual raw client record that timestamp came from — needed
               later by the "Deleted/Edited" invoice check (3.4 / B5), which
               reads fields off that specific raw row rather than the
               merged/aggregated entry fields. ---------- */
            const posSync = posItem?.LastSyncDateTime ? new Date(posItem.LastSyncDateTime) : null;
            const serviceSync = serviceItem?.LastSyncDateTime ? new Date(serviceItem.LastSyncDateTime) : null;

            entry.LastSeenAt =
                posSync && serviceSync
                    ? (posSync > serviceSync ? posItem.LastSyncDateTime : serviceItem.LastSyncDateTime)
                    : (posItem?.LastSyncDateTime || serviceItem?.LastSyncDateTime || null);

            const latestRawItem =
                posSync && serviceSync
                    ? (posSync > serviceSync ? posItem : serviceItem)
                    : (posItem || serviceItem);

            const isServerMachine = entry.IsServerMachine === 1;

            /* ============================================================
               3.3  OFFLINE DETERMINATION  — the ONLY rule for the Offline tab
               ------------------------------------------------------------
               No log at all => offline.
               Otherwise: offline iff (goClickTime - LastSeenAt) > OFFLINE_THRESHOLD_HOURS.
               Applies the same way to server AND non-server machines.
            ============================================================ */
            const lastSeenTime = entry.LastSeenAt ? new Date(entry.LastSeenAt).getTime() : null;
            const diffMs = lastSeenTime !== null ? (goClickTime - lastSeenTime) : null;

            // display-only breakdown
            const diffHours = diffMs !== null ? Math.floor(diffMs / (60 * 60 * 1000)) : null;
            const diffMinutes = diffMs !== null ? Math.floor((diffMs % (60 * 60 * 1000)) / (60 * 1000)) : null;
            entry.OfflineGapHours = diffHours;
            entry.OfflineGapMinutes = diffMinutes;

            // actual offline decision — setting is in MINUTES, compare raw ms gap (no truncation)
            const offlineThresholdMs = (Number(loginSystemSetting().POSDashboardTime) || 0) * 60 * 1000;
            entry.IsOffline = (diffMs === null || diffMs > offlineThresholdMs) ? 1 : 0;

            /* ============================================================
               3.4  OK DETERMINATION — the full rule for the "All OK" tab
               ------------------------------------------------------------
               Offline machines are already excluded via the IsOffline guard
               below (see 3.3) — so there's no separate "log found within X"
               check here. Everything below assumes the machine is already
               confirmed online; it's purely about version + sync correctness.
            
               IsServer = 1:
                 B1. POS version correct        (running === current)
                 B2. Service version correct    (running === current)
                 B3. Latest SERVICE log (IsService=1) found within
                     POSDasboardSyncFrequency minutes of goClickTime, checked
                     on the raw service item's own LastSyncDateTime.
                     No qualifying service log at all => fails.
                 B4. Invoice sync correct (Regular): on latestRawItem (whichever
                     of POS/Service has the newer LastSyncDateTime) — gap between
                     its own MaxInvoiceCreatedOn and ERPMaxInvoiceCreatedOn is
                     under InvoiceSyncFrequency + 2 minutes.
                 B5. Invoice sync correct (Deleted/Edited): same rule, same
                     latestRawItem, but on MaxDeletedInvoiceCreatedOn /
                     ERPDeleteMaxInvoiceCreatedOn.
            
               IsServer = 0:
                 B1 only — POS version correct. Nothing else applies.
            
               A machine is OK only if NOT offline AND every applicable part
               of Condition B passes.
            ============================================================ */
            const posVersion = entry.Versions.find(v => v.label === "POS");
            const serviceVersion = entry.Versions.find(v => v.label === "Service");
            const posVersionOk = !posVersion || !isUpdateRequiredLocal(posVersion.running, posVersion.current);
            // B2 — Service version correct
            const serviceVersionOk = !serviceVersion || !isUpdateRequiredLocal(serviceVersion.running, serviceVersion.current);
            let conditionB;

            if (isServerMachine) {

                // B3 — latest SERVICE log (IsService=1) within POSDasboardSyncFrequency minutes
                const posDashSyncFreqMinutes = Number(entry.POSDasboardSyncFrequency) || 1440;
                const hasQualifyingServiceLog =
                    serviceItem && Number(serviceItem.IsService) === 1 && serviceItem.LastSyncDateTime;
                const serviceLogWithinFrequency = hasQualifyingServiceLog
                    ? (goClickTime - new Date(serviceItem.LastSyncDateTime).getTime()) <= posDashSyncFreqMinutes * 60 * 1000
                    : false; // no qualifying service log => fails

                // B4 + B5 — invoice sync correct (Regular & Deleted/Edited), both checked
                // on latestRawItem (whichever of POS/Service has the newer LastSyncDateTime)
                const invoiceSyncFreqMinutes = Number(entry.SyncTime) || 5;
                const allowedGapMinutes = invoiceSyncFreqMinutes + 2;

                const invoiceRegularOk = isWithinSyncFrequencyLocal(
                    latestRawItem?.MaxInvoiceCreatedOn,
                    latestRawItem?.ERPMaxInvoiceCreatedOn,
                    allowedGapMinutes
                );
                const invoiceDeletedOk = isWithinSyncFrequencyLocal(
                    latestRawItem?.MaxDeletedInvoiceCreatedOn,
                    latestRawItem?.ERPDeleteMaxInvoiceCreatedOn,
                    allowedGapMinutes
                );


                conditionB = posVersionOk && serviceVersionOk && serviceLogWithinFrequency && invoiceRegularOk && invoiceDeletedOk;
            } else {
                // IsServer = 0 — only POS version needs to be correct
                conditionB = posVersionOk && serviceVersionOk;
            }

            // Offline machines can never be OK — matches: Total -> minus Offline -> filtered for OK -> leftover is Needs Attention.
            entry.IsOk = (entry.IsOffline === 0 && conditionB) ? 1 : 0;

            /* ============================================================
               3.5  ERP INVOICE / DELETE MISMATCH  (server machines only)
               — unrelated to Offline/OK, feeds the Invoice/Delete chips
                 and "Bill Number/Time Wise" sub-tabs.
            ============================================================ */
            const invoiceSyncFreqMinutes = Number(entry.SyncTime) || 5;
            const allowedGapMinutes = invoiceSyncFreqMinutes + 2;
            const invoiceDateMismatch = isServerMachine && isDateMismatchLocal(
                entry.MaxInvoiceCreatedOn,
                entry.ERPMaxInvoiceCreatedOn,
                allowedGapMinutes
            );
            const deleteDateMismatch = isServerMachine && isDateMismatchLocal(
                entry.MaxDeletedInvoiceCreatedOn,
                entry.ERPDeleteMaxInvoiceCreatedOn,
                allowedGapMinutes
            );

            /* ---------- 3.6 Flags / VersionMismatchList assembly ---------- */
            if (
                entry.IsOffline === 1 ||
                entry.VersionMismatchList.length > 0 ||
                invoiceDateMismatch ||
                deleteDateMismatch
            ) {
                entry.HasSyncIssue = true;
            }

            if (entry.IsOffline === 1) {
                entry.VersionMismatchList.push("OFFLINE");
            }
            if (invoiceDateMismatch) {
                entry.VersionMismatchList.push("INVOICE SYNC PENDING");
                entry.VersionMismatchList.push("ERP INVOICE DATE MISMATCH");
            }
            if (deleteDateMismatch) {
                entry.VersionMismatchList.push("DELETE SYNC PENDING");
                entry.VersionMismatchList.push("ERP DELETE NOT SYNCED");
            }

            delete entry.PosItem;
            delete entry.ServiceItem;

            if (entry.VersionMismatchList.length === 0) {
                entry.VersionMismatchList = ["OK"];
            }
        });

        /* ---------- FINAL RESPONSE ---------- */
        response.Data = Object.values(merged);
        // Separate, unmerged list — only for the "Service Not Working" sub-tab
        response.ServiceRows = serviceOnlyRows;
        response.Type = config.btnId;

        yield put(PosDashboard_GoButton_Success(response));
    } catch (error) {
        console.error("❌ POS Dashboard Saga Error:", error);
        yield put(PosDashboard_ApiErrorAction());
    }
}

/* ---------- WATCHER ---------- */
export default function* PosDashboard_Saga() {
    yield takeLatest(POS_DASHBOARD_GO_BUTTON_ACTION, PosDashboard_GenFunc);
}